/**
 * Type-safe query key factory.
 * All TanStack Query keys are defined here — single source of truth.
 *
 * Usage:
 *   queryKeys.bots.all()          → ['bots']
 *   queryKeys.bots.detail('123')  → ['bots', '123']
 *   queryKeys.bots.status('123')  → ['bots', '123', 'status']
 */

export const queryKeys = {
  // Auth
  auth: {
    me: () => ['auth', 'me'] as const,
  },

  // Bots
  bots: {
    all: () => ['bots'] as const,
    list: (params?: Record<string, unknown>) => ['bots', 'list', params] as const,
    detail: (id: string) => ['bots', id] as const,
    status: (id: string) => ['bots', id, 'status'] as const,
    config: (id: string) => ['bots', id, 'config'] as const,
    configHistory: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'config', 'history', params] as const,
    orders: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'orders', params] as const,
    fills: (id: string, params?: Record<string, unknown>) => ['bots', id, 'fills', params] as const,
    positions: (id: string) => ['bots', id, 'positions'] as const,
    equity: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'equity', params] as const,
    pnl: (id: string, params?: Record<string, unknown>) => ['bots', id, 'pnl', params] as const,
    signals: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'signals', params] as const,
    riskLog: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'risk-log', params] as const,
    events: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'events', params] as const,
    portfolio: (id: string) => ['bots', id, 'portfolio'] as const,
    analytics: (id: string, window?: string) => ['bots', id, 'analytics', window] as const,
    executionQuality: (id: string, window?: number) =>
      ['bots', id, 'execution-quality', window] as const,
    summary: (id: string) => ['bots', id, 'summary'] as const,
    batchStatus: () => ['bots', 'batch-status'] as const,
    attribution: (id: string, window?: string) => ['bots', id, 'attribution', window] as const,
    drift: (id: string, window?: string) => ['bots', id, 'drift', window] as const,
    configSnapshots: (id: string) => ['bots', id, 'config', 'snapshots'] as const,
    configSnapshotsDiff: (id: string, params?: Record<string, unknown>) =>
      ['bots', id, 'config', 'snapshots', 'diff', params] as const,
  },

  // Exchange Accounts
  exchangeAccounts: {
    all: () => ['exchange-accounts'] as const,
    list: (params?: Record<string, unknown>) => ['exchange-accounts', 'list', params] as const,
    detail: (id: string) => ['exchange-accounts', id] as const,
    liveStatus: (id: string) => ['exchange-accounts', id, 'live-status'] as const,
    liveHistory: (id: string, params?: Record<string, unknown>) =>
      ['exchange-accounts', id, 'live-history', params] as const,
    config: (id: string) => ['exchange-accounts', id, 'config'] as const,
    balance: (id: string) => ['exchange-accounts', id, 'balance'] as const,
    symbols: (id: string) => ['exchange-accounts', id, 'symbols'] as const,
    metadata: (platform: string) => ['exchange-accounts', 'metadata', platform] as const,
  },

  // Strategies
  strategies: {
    all: () => ['strategies'] as const,
    schema: (type: string) => ['strategies', type, 'schema'] as const,
  },

  // Orders (cross-bot)
  orders: {
    all: () => ['orders'] as const,
    list: (params?: Record<string, unknown>) => ['orders', 'list', params] as const,
  },

  // Positions (cross-bot)
  positions: {
    all: () => ['positions'] as const,
    list: (params?: Record<string, unknown>) => ['positions', 'list', params] as const,
  },

  // Alerts (LOK-1106)
  alerts: {
    all: () => ['alerts'] as const,
    list: () => ['alerts', 'list'] as const,
  },

  // Portfolio
  portfolio: {
    summary: () => ['portfolio', 'summary'] as const,
    equityCurve: (params?: Record<string, unknown>) =>
      ['portfolio', 'equity-curve', params] as const,
    alerts: () => ['portfolio', 'alerts'] as const,
    account: (id: string) => ['portfolio', 'account', id] as const,
  },

  // Preferences & Risk Policy (current user)
  preferences: {
    me: () => ['preferences', 'me'] as const,
  },
  riskPolicy: {
    me: () => ['risk-policy', 'me'] as const,
  },

  // Loki API keys (current user) — LOK-1113
  apiKeys: {
    all: () => ['api-keys'] as const,
    list: () => ['api-keys', 'list'] as const,
  },

  // Notification channels (current user) — LOK-1113
  notificationChannels: {
    all: () => ['notification-channels'] as const,
    list: () => ['notification-channels', 'list'] as const,
  },

  // Workspace governance — LOK-1113
  workspace: {
    me: () => ['workspace', 'me'] as const,
  },

  // System
  system: {
    health: () => ['system', 'health'] as const,
    metrics: () => ['system', 'metrics'] as const,
    risk: () => ['system', 'risk'] as const,
    exchanges: () => ['system', 'exchanges'] as const,
    exchangesHealth: () => ['system', 'exchanges', 'health'] as const,
    exchangesMaintenance: () => ['system', 'exchanges', 'maintenance'] as const,
    dataQuality: () => ['system', 'data-quality'] as const,
    killSwitch: () => ['system', 'kill-switch'] as const,
    killSwitchUser: (userId: string) => ['system', 'kill-switch', 'user', userId] as const,
    exchangeCB: () => ['system', 'exchange-cb'] as const,
    exchangeCBDetail: (id: string) => ['system', 'exchange-cb', id] as const,
    confirmationToken: () => ['system', 'confirmation-token'] as const,
  },

  // Backtests
  backtests: {
    all: () => ['backtests'] as const,
    list: (params?: Record<string, unknown>) => ['backtests', 'list', params] as const,
    detail: (id: string) => ['backtests', 'detail', id] as const,
    equityCurve: (id: string) => ['backtests', 'equityCurve', id] as const,
    compare: (ids: string[]) => ['backtests', 'compare', ...ids] as const,
    status: (id: string) => ['backtests', 'status', id] as const,
    annotations: (id: string) => ['backtests', 'annotations', id] as const,
  },

  // Audit
  audit: {
    all: () => ['audit'] as const,
    list: (params?: Record<string, unknown>) => ['audit', 'list', params] as const,
  },

  // Reconciliation
  reconciliation: {
    all: () => ['reconciliation'] as const,
    list: (params?: Record<string, unknown>) => ['reconciliation', 'list', params] as const,
    latest: () => ['reconciliation', 'latest'] as const,
  },

  // MFA
  mfa: {
    status: () => ['mfa', 'status'] as const,
  },

  // Sessions
  sessions: {
    all: () => ['sessions'] as const,
    list: () => ['sessions', 'list'] as const,
  },

  // Venue Connections (replaces exchangeAccounts)
  venueConnections: {
    all: () => ['venue-connections'] as const,
    list: (params?: Record<string, unknown>) => ['venue-connections', 'list', params] as const,
    detail: (id: string) => ['venue-connections', id] as const,
    liveStatus: (id: string) => ['venue-connections', id, 'live-status'] as const,
    liveHistory: (id: string, params?: Record<string, unknown>) =>
      ['venue-connections', id, 'live-history', params] as const,
    config: (id: string) => ['venue-connections', id, 'config'] as const,
    balance: (id: string) => ['venue-connections', id, 'balance'] as const,
    symbols: (id: string) => ['venue-connections', id, 'symbols'] as const,
    rotateStatus: (id: string) => ['venue-connections', id, 'rotate-status'] as const,
    listWithBalances: (params?: Record<string, unknown>) =>
      ['venue-connections', 'list-with-balances', params] as const,
    allocationCapacity: (id: string) => ['venue-connections', id, 'allocation-capacity'] as const,
  },

  // Execution Profiles
  executionProfiles: {
    all: () => ['execution-profiles'] as const,
    list: (params?: Record<string, unknown>) => ['execution-profiles', 'list', params] as const,
    detail: (id: string) => ['execution-profiles', id] as const,
  },

  // Venue Risk Caps
  venueRiskCaps: {
    all: () => ['venue-risk-caps'] as const,
    list: (params?: Record<string, unknown>) => ['venue-risk-caps', 'list', params] as const,
  },

  // Strategy Configs
  strategyConfigs: {
    all: () => ['strategy-configs'] as const,
    list: (params?: Record<string, unknown>) => ['strategy-configs', 'list', params] as const,
    detail: (id: string) => ['strategy-configs', id] as const,
    versions: (id: string) => ['strategy-configs', id, 'versions'] as const,
    versionsDiff: (id: string, params?: Record<string, unknown>) =>
      ['strategy-configs', id, 'versions', 'diff', params] as const,
  },

  // Strategy Templates (catalog)
  strategyTemplates: {
    all: () => ['strategy-templates'] as const,
    list: (params?: Record<string, unknown>) => ['strategy-templates', 'list', params] as const,
  },

  // Config Engine
  configEngine: {
    templates: () => ['config', 'templates'] as const,
    template: (key: string) => ['config', 'templates', key] as const,
  },

  // Wallets
  wallets: {
    all: () => ['wallets'] as const,
    list: (params?: Record<string, unknown>) => ['wallets', 'list', params] as const,
    balances: (id: string) => ['wallets', id, 'balances'] as const,
    treasury: (params?: Record<string, unknown>) => ['wallets', 'treasury', params] as const,
  },

  // Ledger
  ledger: {
    all: () => ['ledger'] as const,
    list: (params?: Record<string, unknown>) => ['ledger', 'list', params] as const,
    consistency: (walletId: string) => ['ledger', 'consistency', walletId] as const,
  },

  // Market Data
  marketData: {
    health: () => ['market-data', 'health'] as const,
    symbolHealth: (symbol: string) => ['market-data', 'health', symbol] as const,
    subscriptions: () => ['market-data', 'subscriptions'] as const,
    wsStatus: () => ['market-data', 'ws', 'status'] as const,
    orderBook: (symbol: string) => ['market-data', symbol, 'order-book'] as const,
    fundingRate: (symbol: string) => ['market-data', 'funding-rate', symbol] as const,
    markPrice: (symbol: string) => ['market-data', 'mark-price', symbol] as const,
  },

  // Feature Flags
  featureFlags: {
    all: () => ['feature-flags'] as const,
    list: (params?: Record<string, unknown>) => ['feature-flags', 'list', params] as const,
    detail: (id: string) => ['feature-flags', id] as const,
    check: (name: string) => ['feature-flags', 'check', name] as const,
  },

  // Security Events
  securityEvents: {
    all: () => ['security-events'] as const,
    list: (params?: Record<string, unknown>) => ['security-events', 'list', params] as const,
  },

  // Audit Retention
  auditRetention: {
    stats: () => ['audit', 'retention-stats'] as const,
  },

  // Users
  users: {
    all: () => ['users'] as const,
    list: (params?: Record<string, unknown>) => ['users', 'list', params] as const,
    detail: (id: string) => ['users', id] as const,
    usage: (id: string) => ['users', id, 'usage'] as const,
    preferences: (id: string) => ['users', id, 'preferences'] as const,
    riskPolicy: (id: string) => ['users', id, 'risk-policy'] as const,
    onboardingStatus: (id: string) => ['users', id, 'onboarding-status'] as const,
    onboardingSummary: () => ['users', 'onboarding-summary'] as const,
  },

  // DLQ (admin)
  dlq: {
    all: () => ['dlq'] as const,
    list: (params?: Record<string, unknown>) => ['dlq', 'list', params] as const,
    detail: (id: string) => ['dlq', id] as const,
  },

  // Signal Analytics (cross-bot)
  signalAnalytics: {
    all: () => ['signal-analytics'] as const,
    timeSeries: (params?: Record<string, unknown>) =>
      ['signal-analytics', 'time-series', params] as const,
    attribution: () => ['signal-analytics', 'attribution'] as const,
    byStrategy: () => ['signal-analytics', 'by-strategy'] as const,
  },

  // My (current user)
  me: {
    onboardingStatus: () => ['me', 'onboarding-status'] as const,
  },
} as const;

/** Polling intervals in milliseconds — per spec */
export const POLLING_INTERVALS = {
  botStatus: 5_000,
  activeOrders: 10_000,
  systemHealth: 15_000,
  portfolioSummary: 15_000,
  positions: 15_000,
  exchangeAccounts: 30_000,
  exchangeAccountDetail: 30_000,
  liveStatus: 15_000,
  systemRisk: 30_000,
  systemMetrics: 30_000,
  exchangesHealth: 30_000,
  dataQuality: 30_000,
  equity: 30_000,
  pnl: 60_000,
  exchangeBalance: 15_000,
  symbolCatalog: 60 * 60_000, // 1 hour
  venueConnections: 30_000,
  venueConnectionDetail: 30_000,
  venueConnectionBalance: 15_000,
  killSwitch: 15_000,
  exchangeCB: 30_000,
  marketDataHealth: 30_000,
  wsStatus: 15_000,
  orderBook: 5_000,
  markPrice: 10_000,
  portfolioAlerts: 60_000,
  alerts: 30_000,
  rotateStatus: 3_000,
  backtestStatus: 5_000,
  walletTreasury: 60_000,
  backtestRunningList: 5_000,
  backtestKpi: 15_000,
  backtestList: 30_000,
  backtestAnnotations: 30_000,
} as const;
